import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Save, Loader2 } from "lucide-react";
import workvivoNotificationsService from "../../services/workvivoNotificationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";

const TYPE_LABELS = {
  recognition_created: "Recognition created",
  recognition_approved: "Recognition approved",
  recognition_rejected: "Recognition rejected",
  nomination_submitted: "Nomination submitted",
  nomination_approved: "Nomination approved",
  campaign_started: "Campaign started",
  campaign_ended: "Campaign ended",
  budget_exhausted: "Budget exhausted",
  wallet_credited: "Wallet credited",
  reward_redeemed: "Reward redeemed",
  approval_pending: "Approval pending",
  system_alert: "System alert",
};

const WorkvivoNotificationMatrix = ({ orgId }) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["workvivo-notifications", "config", orgId || "self"],
    queryFn: () => workvivoNotificationsService.getConfig(orgId),
  });

  // Seed local edits from the server once per org; later background
  // refetches must not wipe unsaved changes.
  const seededOrgRef = useRef(null);
  useEffect(() => {
    const key = orgId || "self";
    if (data && seededOrgRef.current !== key) {
      setRows(data);
      seededOrgRef.current = key;
    }
  }, [data, orgId]);

  const saveMutation = useMutation({
    mutationFn: (next) => workvivoNotificationsService.updateConfig(next, orgId),
    onSuccess: (updated) => {
      setRows(updated);
      queryClient.invalidateQueries({ queryKey: ["workvivo-notifications", "config"] });
      toast.success("Notification routing saved");
    },
    onError: (err) => toast.error(formatError(err)),
  });

  const dirty = useMemo(() => {
    if (!data) return false;
    return JSON.stringify(data) !== JSON.stringify(rows);
  }, [data, rows]);

  const toggle = (type, field) => {
    setRows((prev) =>
      prev.map((r) => (r.type === type ? { ...r, [field]: !r[field] } : r)),
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading routing...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
        {formatError(error)}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Workvivo notification routing
          </h3>
        </div>
        <button
          type="button"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate(rows)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-2">
        Pick which in-app notifications also fire on Workvivo (push and/or
        email). The in-app bell always fires regardless.
      </p>
      <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
        <strong>Heads up:</strong> the Workvivo API token must have the{" "}
        <code className="px-1 py-0.5 bg-amber-100 rounded text-xs">
          notifications.write
        </code>{" "}
        and{" "}
        <code className="px-1 py-0.5 bg-amber-100 rounded text-xs">
          notifications.users.write
        </code>{" "}
        scopes granted. Without them every dispatch silently 403s. In-app
        notifications still work, but no Workvivo push or email goes out.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Notification type</th>
              <th className="py-2 px-2 font-medium text-center">Enabled</th>
              <th className="py-2 px-2 font-medium text-center">Push</th>
              <th className="py-2 px-2 font-medium text-center">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.type} className="border-b last:border-b-0">
                <td className="py-2 pr-4 text-gray-800">
                  {TYPE_LABELS[row.type] || row.type}
                </td>
                {["enabled", "push", "email"].map((field) => (
                  <td key={field} className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!row[field]}
                      onChange={() => toggle(row.type, field)}
                      disabled={field !== "enabled" && !row.enabled}
                      className="w-4 h-4 accent-indigo-600 disabled:opacity-40"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkvivoNotificationMatrix;
